import React, { memo, useMemo } from 'react';
import {
  ComposedChart, LineChart, Line, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceArea, ReferenceLine,
} from 'recharts';
import { Trophy, Activity, BatteryCharging, Gauge, Cog, Zap, Layers, Sigma, Scale } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import ReferenceLineTopLabel, { REFERENCE_LABEL_TOP_MARGIN } from './ReferenceLineTopLabel';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, CHART_TEXT, AXIS_TICK_STYLE, AXIS_LABEL_STYLE,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './chartTheme';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { STRATEGY_KEYS, STRATEGY_LABELS, MODE_ORDER, MODE_SHORT } from '../../../lib/hevEnergyTools';

// Strategy identity colours (fixed order from the validated site palette); the ICE baseline is
// the neutral reference.
export const STRATEGY_COLORS = { rule: SERIES_COLORS[0], ecms: SERIES_COLORS[1], ice: '#64748B' };
export const MODE_COLORS = {
  stop: '#CBD5E1', idle: '#94A3B8', ev: SERIES_COLORS[2], engine: SERIES_COLORS[1], assist: SERIES_COLORS[3],
  charge: SERIES_COLORS[6], regen: SERIES_COLORS[0], brake: SERIES_COLORS[4],
};
const SYNC_ID = 'hev-traces';
const fmtT = (v) => Number(v).toFixed(0);
const fmt = (d) => (v) => (v == null ? '—' : Number(v).toFixed(d));
// Legend text stays in the muted text colour (the swatch carries identity), in a fixed order.
const legendText = (value) => <span style={{ color: CHART_TEXT }}>{value}</span>;
const LEGEND_ORDER = ['Rule-based', 'ECMS', 'ICE baseline'];
const legendSorter = (item) => {
  const i = LEGEND_ORDER.indexOf(item.value);
  return i < 0 ? 99 : i;
};
const leftLabel = (value) => ({ value, angle: -90, position: 'insideLeft', style: { ...AXIS_LABEL_STYLE, textAnchor: 'middle' } });

// --- Summary -------------------------------------------------------------------------------------

const Row = ({ label, children, testId }) => (
  <div className="flex items-baseline justify-between gap-3 py-1 border-b border-border/50 last:border-0">
    <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="font-mono text-sm text-right" data-testid={testId}>{children}</span>
  </div>
);

export function HevSummaryPanel({ summary, anchor, cycle }) {
  const by = Object.fromEntries(summary.map((s) => [s.strategy, s]));
  return (
    <ChartCard icon={Trophy} title="Results by Strategy" testId="hev-summary"
      description={`${cycle.label}: ${cycle.distance_km.toFixed(2)} km in ${cycle.duration_s.toFixed(0)} s. Fuel is SoC-corrected (SAE J1711 charge-sustaining check).`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {STRATEGY_KEYS.map((key) => {
          const s = by[key];
          if (!s) return null;
          return (
            <div key={key} className="rounded-md border border-border p-3" data-testid={`hev-summary-${key}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-3 w-3 rounded-sm shrink-0" style={{ backgroundColor: STRATEGY_COLORS[key] }} />
                <span className="font-chivo font-bold uppercase tracking-tight">{s.label}</span>
              </div>
              <p className="font-mono text-2xl font-bold text-autoxium-blue" data-testid={`hev-fuel-${key}`}>
                {s.fuel_corrected_l_per_100km.toFixed(2)} <span className="text-sm font-normal text-muted-foreground">L/100 km</span>
              </p>
              <p className="font-mono text-xs text-muted-foreground mb-2">
                {s.co2_g_per_km.toFixed(0)} g CO₂/km · raw {s.fuel_raw_l_per_100km.toFixed(2)} L/100 km
              </p>
              {s.saving_vs_ice_pct != null && <Row label="Saving vs ICE" testId={`hev-saving-${key}`}>{s.saving_vs_ice_pct.toFixed(1)} %</Row>}
              {s.delta_soc_pct != null && (
                <Row label="Final ΔSoC">
                  {s.delta_soc_pct >= 0 ? '+' : ''}{s.delta_soc_pct.toFixed(2)} pts{' '}
                  <span className={s.charge_sustaining ? 'text-muted-foreground' : 'text-amber-600'}>
                    ({s.charge_sustaining ? 'CS' : 'not CS'}, NEC {s.nec_pct_of_fuel >= 0 ? '+' : ''}{s.nec_pct_of_fuel.toFixed(2)} %)
                  </span>
                </Row>
              )}
              <Row label="Engine on">{s.engine_on_pct.toFixed(1)} % · {s.engine_starts} starts</Row>
              <Row label="Avg engine efficiency">{s.avg_engine_efficiency_pct.toFixed(1)} %</Row>
              {key !== 'ice' && <Row label="Regen recovered">{(s.regen_recovered_kwh * 1000).toFixed(0)} Wh</Row>}
              {s.equivalence_factor != null && <Row label="Equivalence factor s" testId="hev-s-value">{s.equivalence_factor.toFixed(3)}{s.s_iterations ? ` (${s.s_iterations} runs)` : ''}</Row>}
              {s.trace_miss_s > 0 && <Row label="Trace missed"><span className="text-amber-600">{s.trace_miss_s.toFixed(0)} s</span></Row>}
            </div>
          );
        })}
      </div>
      {anchor && (
        <div className="mt-4 rounded-md bg-muted/40 p-3 text-xs text-muted-foreground space-y-1" data-testid="hev-anchor">
          <p>
            <span className="font-semibold text-foreground">Published reference:</span> {anchor.value.toFixed(2)} L/100 km ({anchor.label}).
            {' '}Rule-based {((by.rule.fuel_corrected_l_per_100km / anchor.value - 1) * 100).toFixed(1)} %,
            {' '}ECMS {((by.ecms.fuel_corrected_l_per_100km / anchor.value - 1) * 100).toFixed(1)} % against it.
          </p>
          <p>{anchor.explain}</p>
        </div>
      )}
    </ChartCard>
  );
}

// --- Time traces (one measure per panel, shared time axis, synced crosshair, shared zoom) ------

const TRACE_HEIGHT = 190;

const TracePanel = memo(function TracePanel({ data, zoom, onHover, yLabel, lines, refLines, yDomain, yTicks, step, decimals = 2, height = TRACE_HEIGHT, testId, area }) {
  const hasTopLabels = refLines && refLines.length > 0;
  return (
    <div data-testid={testId}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} syncId={SYNC_ID} syncMethod="value"
          margin={{ top: hasTopLabels ? REFERENCE_LABEL_TOP_MARGIN : 6, right: 12, left: 0, bottom: 2 }}
          onMouseDown={zoom.onMouseDown}
          onMouseMove={(e) => { zoom.onMouseMove(e); if (e && e.activeLabel !== undefined) onHover(e.activeLabel); }}
          onMouseUp={zoom.onMouseUp}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis dataKey="t" type="number" domain={zoom.xDomain} allowDataOverflow tick={AXIS_TICK_STYLE}
            axisLine={{ stroke: CHART_GRID }} tickLine={false} tickFormatter={fmtT} height={22} />
          <YAxis tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} label={leftLabel(yLabel)}
            domain={yDomain || ['auto', 'auto']} ticks={yTicks} allowDecimals={!step} tickFormatter={(v) => (Number.isInteger(v) ? String(v) : Number(v).toFixed(1))} />
          {/* Synced panels place their tooltip at the top, following the cursor, clear of the axis. */}
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={(v) => `t = ${fmtT(v)} s`}
            formatter={fmt(decimals)} position={{ y: 0 }} offset={14} itemSorter={legendSorter} />
          {lines.length > 1 && <Legend wrapperStyle={{ ...LEGEND_STYLE, paddingTop: 0 }} height={20} formatter={legendText} itemSorter={legendSorter} />}
          {(refLines || []).map((r) => (
            <ReferenceLine key={r.label} y={r.y} stroke={r.color || CHART_AXIS} strokeDasharray="4 3"
              label={<ReferenceLineTopLabel text={r.label} color={CHART_AXIS} anchor="start" />} ifOverflow="extendDomain" />
          ))}
          {lines.map((l) => (area ? (
            <Area key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} fill={l.color} fillOpacity={0.12}
              strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls={false} />
          ) : (
            <Line key={l.key} type={step ? 'stepAfter' : 'linear'} dataKey={l.key} name={l.name} stroke={l.color}
              strokeWidth={1.5} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} connectNulls={false} />
          )))}
          {zoom.isSelecting && (
            <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

const PanelTitle = ({ icon: Icon, children }) => (
  <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mt-4 mb-1 flex items-center gap-1.5">
    <Icon className="h-3.5 w-3.5" /> {children}
  </p>
);

export function useTraceRows(series) {
  return useMemo(() => {
    const st = series.strategies;
    return series.time_s.map((t, i) => {
      const row = { t, speed: series.speed_kph[i] };
      for (const k of STRATEGY_KEYS) {
        const s = st[k];
        row[`soc_${k}`] = s.soc_pct[i];
        row[`eng_${k}`] = s.engine_kw[i];
        row[`mot_${k}`] = s.motor_kw[i];
        row[`bat_${k}`] = s.battery_kw[i];
        row[`gear_${k}`] = s.gear[i] || null;
      }
      if (st.ecms.equivalence_factor) row.s = st.ecms.equivalence_factor[i];
      return row;
    });
  }, [series]);
}

const strategyLines = (prefix, keys) => keys.map((k) => ({ key: `${prefix}_${k}`, name: STRATEGY_LABELS[k], color: STRATEGY_COLORS[k] }));

export const HevTimeTraces = memo(function HevTimeTraces({ rows, zoom, onHover, components, hybrid, hasS }) {
  const hybridKeys = hybrid ? ['rule', 'ecms'] : [];
  const sTop = hasS && rows.length ? Math.max(1, Math.ceil((rows[0].s || 0) + 0.5)) : 1;
  return (
    <ChartCard icon={Activity} title="Time Traces" testId="hev-time-traces"
      description="All panels share one time axis: hovering one shows the same instant in all of them and moves the architecture diagram; drag across any panel to zoom them all.">
      <ChartZoomHeader caption="Shared time axis (s)" isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="hev-zoom-reset" />
      <PanelTitle icon={Gauge}>Vehicle speed (km/h)</PanelTitle>
      <TracePanel data={rows} zoom={zoom} onHover={onHover} yLabel="km/h" area decimals={1} testId="hev-trace-speed"
        lines={[{ key: 'speed', name: 'Speed', color: SERIES_COLORS[5] }]} />
      {hybrid && (
        <>
          <PanelTitle icon={BatteryCharging}>Battery state of charge (%)</PanelTitle>
          <TracePanel data={rows} zoom={zoom} onHover={onHover} yLabel="SoC %" decimals={1} testId="hev-trace-soc"
            lines={strategyLines('soc', hybridKeys)}
            refLines={[
              { y: components.soc_max_pct, label: `Max ${components.soc_max_pct}%` },
              { y: components.soc_min_pct, label: `Min ${components.soc_min_pct}%` },
            ]} />
        </>
      )}
      <PanelTitle icon={Cog}>Engine power (kW)</PanelTitle>
      <TracePanel data={rows} zoom={zoom} onHover={onHover} yLabel="kW" testId="hev-trace-engine" lines={strategyLines('eng', STRATEGY_KEYS)} />
      {hybrid && (
        <>
          <PanelTitle icon={Zap}>Motor mechanical power (kW, + drive / − generate)</PanelTitle>
          <TracePanel data={rows} zoom={zoom} onHover={onHover} yLabel="kW" testId="hev-trace-motor" lines={strategyLines('mot', hybridKeys)} />
          <PanelTitle icon={BatteryCharging}>Battery power (kW, + discharge / − charge)</PanelTitle>
          <TracePanel data={rows} zoom={zoom} onHover={onHover} yLabel="kW" testId="hev-trace-battery" lines={strategyLines('bat', hybridKeys)} />
        </>
      )}
      <PanelTitle icon={Layers}>Gear</PanelTitle>
      <TracePanel data={rows} zoom={zoom} onHover={onHover} yLabel="gear" step decimals={0} testId="hev-trace-gear" height={150}
        lines={strategyLines('gear', STRATEGY_KEYS)} yDomain={[0, 'dataMax']} />
      {hasS && (
        <>
          <PanelTitle icon={Sigma}>ECMS equivalence factor s (constant, tuned by bisection)</PanelTitle>
          <TracePanel data={rows} zoom={zoom} onHover={onHover} yLabel="s" testId="hev-trace-s" height={120}
            lines={[{ key: 's', name: 'ECMS s', color: STRATEGY_COLORS.ecms }]} decimals={3}
            yDomain={[0, sTop]} yTicks={Array.from({ length: sTop + 1 }, (_, i) => i)} />
        </>
      )}
    </ChartCard>
  );
});

// --- Mode band (engine on/off + operating mode per strategy) ----------------------------------

function runs(modes, times) {
  const out = [];
  let start = 0;
  for (let i = 1; i <= modes.length; i += 1) {
    if (i === modes.length || modes[i] !== modes[start]) {
      const tEnd = i < times.length ? times[i] : times[times.length - 1] + (times[1] - times[0] || 1);
      out.push({ mode: modes[start], t0: times[start], t1: tEnd });
      start = i;
    }
  }
  return out;
}

export function HevModeBand({ series, zoom, scrubTime, modeLabels }) {
  const times = series.time_s;
  const lo = typeof zoom.xDomain[0] === 'number' ? zoom.xDomain[0] : times[0];
  const hi = typeof zoom.xDomain[1] === 'number' ? zoom.xDomain[1] : times[times.length - 1];
  const bands = useMemo(() => Object.fromEntries(STRATEGY_KEYS.map((k) => [k, runs(series.strategies[k].mode, times)])), [series, times]);
  const W = 1000;
  const rowH = 22;
  const labelW = 0;
  const x = (t) => labelW + ((t - lo) / (hi - lo || 1)) * (W - labelW);
  const present = new Set(STRATEGY_KEYS.flatMap((k) => series.strategies[k].mode));
  return (
    <ChartCard icon={Layers} title="Operating Mode & Engine On/Off" testId="hev-mode-band"
      description="Each row is one strategy across the cycle (follows the traces' zoom). Engine on = Engine only, Assist, Charge or Idle.">
      <div className="space-y-2">
        {STRATEGY_KEYS.map((k) => (
          <div key={k}>
            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-0.5">{STRATEGY_LABELS[k]}</p>
            {/* svg built with createElement (see HevArchitectureDiagram for why) */}
            {React.createElement('svg', {
              viewBox: `0 0 ${W} ${rowH}`, preserveAspectRatio: 'none', className: 'w-full h-5 block rounded-sm', role: 'img',
              'aria-label': `${STRATEGY_LABELS[k]} operating modes over time`, 'data-testid': `hev-mode-row-${k}`,
            },
            ...bands[k].filter((b) => b.t1 > lo && b.t0 < hi).map((b) => React.createElement('rect', {
              key: `${b.t0}-${b.mode}`, x: Math.max(0, x(Math.max(b.t0, lo))), y: 0,
              width: Math.max(0.5, x(Math.min(b.t1, hi)) - x(Math.max(b.t0, lo))), height: rowH,
              style: { fill: MODE_COLORS[b.mode] },
            }, React.createElement('title', null, `${modeLabels[b.mode] || b.mode}: ${b.t0.toFixed(0)}–${b.t1.toFixed(0)} s`))),
            scrubTime != null && scrubTime >= lo && scrubTime <= hi ? React.createElement('line', {
              key: 'scrub', x1: x(scrubTime), x2: x(scrubTime), y1: 0, y2: rowH, style: { stroke: 'hsl(var(--foreground))', strokeWidth: 3 },
              vectorEffect: 'non-scaling-stroke',
            }) : null)}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3" data-testid="hev-mode-legend">
        {MODE_ORDER.filter((m) => present.has(m)).map((m) => (
          <span key={m} className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: MODE_COLORS[m] }} /> {modeLabels[m] || MODE_SHORT[m]}
          </span>
        ))}
      </div>
    </ChartCard>
  );
}

// --- Energy breakdown -----------------------------------------------------------------------------

// Output sinks grouped to stay within the 8-hue categorical palette; the table lists every row.
const SINK_GROUPS = [
  { key: 'engine', label: 'Engine losses', rows: ['engine_loss'], color: SERIES_COLORS[1] },
  { key: 'drive', label: 'Gearbox, final drive & clutch', rows: ['driveline_loss', 'slip_loss'], color: SERIES_COLORS[3] },
  { key: 'elec', label: 'Motor, inverter & battery losses', rows: ['motor_loss', 'battery_loss'], color: SERIES_COLORS[6] },
  { key: 'aux', label: 'Accessories (+ alternator)', rows: ['aux', 'alternator_loss'], color: SERIES_COLORS[4] },
  { key: 'road', label: 'Road load (rolling + aero)', rows: ['road_load', 'inertia', 'trace_shortfall'], color: SERIES_COLORS[2] },
  { key: 'friction', label: 'Friction brakes', rows: ['friction_brake'], color: SERIES_COLORS[7] },
];

export function HevEnergyBreakdown({ energy, summary }) {
  const by = Object.fromEntries(summary.map((s) => [s.strategy, s]));
  const data = STRATEGY_KEYS.map((k) => {
    const rows = Object.fromEntries(energy[k].rows.map((r) => [r.key, r.kwh]));
    const out = { name: STRATEGY_LABELS[k] };
    for (const g of SINK_GROUPS) out[g.key] = g.rows.reduce((a, r) => a + (rows[r] || 0), 0);
    return out;
  });
  const allRows = energy.rule.rows;
  const cell = (k, key) => {
    const r = energy[k].rows.find((x) => x.key === key);
    return r ? (Math.abs(r.kwh) < 0.0005 ? 0 : r.kwh).toFixed(3) : '—';
  };
  return (
    <ChartCard icon={Scale} title="Energy Breakdown" testId="hev-energy"
      description="Where each strategy's energy went over the cycle. Inputs are fuel energy plus net battery discharge; every output row is a loss or the road load the cycle demands.">
      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }} barCategoryGap={10}>
          <CartesianGrid stroke={CHART_GRID} horizontal={false} />
          <XAxis type="number" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            tickFormatter={(v) => Number(v).toFixed(1)} label={{ value: 'kWh', position: 'insideBottomRight', offset: -2, style: AXIS_LABEL_STYLE }} height={34} />
          <YAxis type="category" dataKey="name" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={86} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} formatter={(v) => `${Number(v).toFixed(3)} kWh`} />
          <Legend wrapperStyle={LEGEND_STYLE} formatter={legendText}
            itemSorter={(item) => SINK_GROUPS.findIndex((g) => g.label === item.value)} />
          {SINK_GROUPS.map((g, i) => (
            <Bar key={g.key} dataKey={g.key} name={g.label} stackId="e" fill={g.color} stroke="#fff" strokeWidth={1}
              radius={i === SINK_GROUPS.length - 1 ? [0, 4, 4, 0] : 0} isAnimationActive={false} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4 overflow-x-auto" data-testid="hev-energy-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-mono text-[11px] uppercase">Energy (kWh)</TableHead>
              {STRATEGY_KEYS.map((k) => <TableHead key={k} className="font-mono text-[11px] uppercase text-right">{STRATEGY_LABELS[k]}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {allRows.map((r) => (
              <TableRow key={r.key}>
                <TableCell className="text-xs">{r.side === 'in' ? 'In · ' : ''}{r.label}</TableCell>
                {STRATEGY_KEYS.map((k) => <TableCell key={k} className="font-mono text-xs text-right">{cell(k, r.key)}</TableCell>)}
              </TableRow>
            ))}
            <TableRow>
              <TableCell className="text-xs font-semibold">Regen recovered (motor shaft)</TableCell>
              {STRATEGY_KEYS.map((k) => <TableCell key={k} className="font-mono text-xs text-right">{by[k].regen_recovered_kwh.toFixed(3)}</TableCell>)}
            </TableRow>
            <TableRow data-testid="hev-energy-balance-row">
              <TableCell className="text-xs font-semibold">Balance check (in − out) / in</TableCell>
              {STRATEGY_KEYS.map((k) => (
                <TableCell key={k} className={`font-mono text-xs text-right ${energy[k].closes ? '' : 'text-destructive'}`}>
                  {energy[k].balance_error_pct.toFixed(4)} % {energy[k].closes ? '✓' : '✗'}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </ChartCard>
  );
}
